import React, { useCallback, useRef, useState } from 'react';
import classNames from 'classnames';
import {
	IconRewindBackward15,
	IconRewindForward15,
	IconMaximize,
	IconMinimize,
	IconX,
	IconLoader2,
	IconAlertCircle,
} from '@tabler/icons-react';
import s from './custom-video-player.module.scss';
import { PlayerIconButton } from './icon-button';
import { PlayPauseIndicator } from './play-pause-indicator';
import { VolumeIndicator } from './volume-indicator';
import { ProgressBar } from './progress-bar';
import { VolumeControl } from './volume-control';
import { PlaybackRateMenu } from './playback-rate-menu';
import { AudioTracksMenu } from './audio-tracks-menu';
import { useMediaState } from '../lib/use-media-state';
import { useIdleControls } from '../lib/use-idle-controls';
import { useFullscreen } from '../lib/use-fullscreen';
import { useVideoHotkeys } from '../lib/use-video-hotkeys';

// Единый no-op-хендлер для блокируемых браузерных действий
// (контекстное меню, drag видео). Защита от скачивания: «Сохранить видео как»
// из контекстного меню и drag-and-drop в файловый менеджер не должны работать.
const preventDefaultHandler = (e) => e.preventDefault();

export const CustomVideoPlayer = ({
	src,
	poster,
	className,
	wrapperClassName,
	style,
	autoPlay = false,
	muted = false,
	loop = false,
	playsInline = true,
	controls = true,
	compact = false,
	initialVolume = 1,
	preload = 'metadata',
	onReady,
	onError,
	onPlay,
	onPause,
	onEnded,
	onClose,
	allowFullscreen = true,
	allowPiP = false,
	allowSpeed = false,
	allowAudioTrack = false,
	audioTracks = [],
	selectedAudioTrack,
	onAudioTrackChange,
	skipSeconds = 15,
	hotkeys = true,
	idleTimeout = 2500,
	showBigPlayButton = true,
	errorMessage = 'Не удалось загрузить видео',
}) => {
	const videoRef = useRef(null);
	const wrapperRef = useRef(null);

	// Рефлы-зеркала стейта громкости: обновляются синхронно в местах записи, чтобы
	// события элемента (volumechange/play/pause) можно было сравнивать с актуальным
	// стейтом без лага на коммит. Стейт — единственный источник истины по громкости:
	// элемент НИКОГДА не «диктует» свои значения обратно (иначе сбросы браузера
	// переподключения к ресурсу попадают в стейт, и ползунок отскакивает к 100%).
	const [isMuted, setIsMuted] = useState(muted);
	const [volume, setVolume] = useState(muted ? 0 : initialVolume);
	const volumeStateRef = useRef(muted ? 0 : initialVolume);
	const mutedStateRef = useRef(muted);
	const setVolumeState = useCallback((value) => {
		volumeStateRef.current = value;
		setVolume(value);
	}, []);
	const setMutedState = useCallback((value) => {
		mutedStateRef.current = value;
		setIsMuted(value);
	}, []);

	// true, пока пользователь удерживает pointer внутри контролов (drag ползунка и т.п.)
	const isInteractingRef = useRef(false);

	const { isPlaying, currentTime, duration, buffered, isLoading, isError } = useMediaState(
		videoRef,
		{ onReady, onError, onPlay, onPause, onEnded, isInteractingRef, volumeStateRef, mutedStateRef },
	);

	const [showSettings, setShowSettings] = useState(false);
	const [showAudioTracks, setShowAudioTracks] = useState(false);
	const menusOpen = showSettings || showAudioTracks;

	const { showControls, setShowControls, resetIdleTimer, markInteracting } = useIdleControls({
		isPlaying,
		menusOpen,
		idleTimeout,
		isInteractingRef,
	});

	const { isFullscreen, toggleFullscreen } = useFullscreen(wrapperRef, allowFullscreen);

	const [isLoop, setIsLoop] = useState(loop);
	const [playbackRate, setPlaybackRate] = useState(1);

	const togglePlay = useCallback(() => {
		const video = videoRef.current;
		if (!video) return;
		if (video.paused || video.ended) {
			video.play().catch(() => {});
		} else {
			video.pause();
		}
	}, []);

	const toggleMute = useCallback(() => {
		const video = videoRef.current;
		if (!video) return;
		const nextMuted = !video.muted;
		// сначала стейт/рефлы, потом элемент: синхронный volumechange от записи
		// должен увидеть совпадение, иначе onVolumeChange «откатит» значение
		setMutedState(nextMuted);
		video.muted = nextMuted;
	}, [setMutedState]);

	const handleVolumeChange = useCallback((e) => {
		const video = videoRef.current;
		if (!video) return;
		const value = Number(e.target.value);
		if (!Number.isFinite(value)) return;
		const nextMuted = value === 0;
		// сначала стейт/рефлы, потом элемент: ползунок не прыгает обратно,
		// а синхронный volumechange не конфликтует с реассёртом
		setVolumeState(value);
		setMutedState(nextMuted);
		video.volume = value;
		video.muted = nextMuted;
	}, [setVolumeState, setMutedState]);

	const handleSeek = useCallback((e) => {
		const video = videoRef.current;
		if (!video) return;
		const value = Number(e.target.value);
		video.currentTime = (value / 100) * duration;
	}, [duration]);

	const skip = useCallback((direction) => {
		const video = videoRef.current;
		if (!video) return;
		video.currentTime = Math.min(Math.max(video.currentTime + direction * skipSeconds, 0), duration);
	}, [duration, skipSeconds]);

	const toggleLoop = useCallback(() => {
		const video = videoRef.current;
		if (!video) return;
		const next = !video.loop;
		video.loop = next;
		setIsLoop(next);
	}, []);

	const changePlaybackRate = useCallback((rate) => {
		const video = videoRef.current;
		if (!video) return;
		video.playbackRate = rate;
		setPlaybackRate(rate);
		setShowSettings(false);
	}, []);

	const selectAudioTrack = useCallback((track) => {
		onAudioTrackChange?.(track);
		setShowAudioTracks(false);
	}, [onAudioTrackChange]);

	useVideoHotkeys({
		videoRef,
		hotkeys,
		duration,
		togglePlay,
		skip,
		toggleMute,
		toggleFullscreen,
		toggleLoop,
		setVolumeState,
		setMutedState,
	});

	const volumeIconState = isMuted || volume === 0 ? 'off' : volume < 0.5 ? 'low' : 'high';

	const handleWrapperClick = (e) => {
		if (e.target.closest(`.${s.controls}`) || e.target.closest(`.${s.menu}`)) return;
		togglePlay();
	};

	const handleDoubleClick = (e) => {
		if (e.target.closest(`.${s.controls}`) || e.target.closest(`.${s.menu}`)) return;
		toggleFullscreen();
	};

	if (isError) {
		return (
			<div className={classNames(s.playerWrapper, wrapperClassName)} style={style}>
				<div className={classNames(s.errorState, className)}>
					<IconAlertCircle size={40} />
					<span>{errorMessage}</span>
				</div>
			</div>
		);
	}

	return (
		<div
			ref={wrapperRef}
			className={classNames(s.playerWrapper, wrapperClassName)}
			style={style}
			onMouseMove={resetIdleTimer}
			onMouseLeave={() => isPlaying && !isInteractingRef.current && setShowControls(false)}
			onFocus={resetIdleTimer}
			onClick={handleWrapperClick}
			onDoubleClick={handleDoubleClick}
			onContextMenu={preventDefaultHandler}
			onTouchStart={resetIdleTimer}
			tabIndex={0}
			role="region"
			aria-label="Видео плеер"
		>
			<video
				ref={videoRef}
				className={classNames(s.video, className)}
				src={src}
				poster={poster}
				autoPlay={autoPlay}
				muted={isMuted}
				loop={isLoop}
				playsInline={playsInline}
				preload={preload}
				controlsList="nodownload noremoteplayback"
				disablePictureInPicture
				onContextMenu={preventDefaultHandler}
				onDragStart={preventDefaultHandler}
			/>

			{isLoading && (
				<div className={s.loaderOverlay}>
					<IconLoader2 className={s.spinner} size={40} />
				</div>
			)}

			{controls && (
				<div
					className={classNames(s.controls, !showControls && s.controlsHidden)}
					onPointerDown={markInteracting}
					onClick={(e) => e.stopPropagation()}
				>
					{/* Top-right extra options (mobile/tablet) */}
					<div className={s.extraOptions}>
						{allowAudioTrack && audioTracks.length > 0 && (
							<AudioTracksMenu
								tracks={audioTracks}
								selected={selectedAudioTrack}
								show={showAudioTracks}
								onToggle={() => setShowAudioTracks((v) => !v)}
								onSelect={selectAudioTrack}
								buttonClassName={s.extraIcon}
							/>
						)}
						{/* Кнопка звука для мобильных (ползунок громкости есть только на десктопе) */}
						<PlayerIconButton
							className={classNames(s.extraIcon, s.mobileOnly)}
							onClick={toggleMute}
							ariaLabel={isMuted ? 'Включить звук' : 'Выключить звук'}
						>
							<VolumeIndicator size={20} state={volumeIconState} />
						</PlayerIconButton>
						{allowFullscreen && (
							<PlayerIconButton
								className={classNames(s.extraIcon, s.mobileOnly)}
								onClick={toggleFullscreen}
								ariaLabel={isFullscreen ? 'Выйти из полноэкранного режима' : 'Полноэкранный режим'}
							>
								{isFullscreen ? <IconMinimize size={20} /> : <IconMaximize size={20} />}
							</PlayerIconButton>
						)}
						{onClose && (
							<PlayerIconButton
								className={classNames(s.extraIcon, s.mobileOnly)}
								onClick={onClose}
								ariaLabel="Закрыть"
							>
								<IconX size={20} />
							</PlayerIconButton>
						)}
					</div>

					{/* Center main controller (mobile/tablet) */}
					<div className={s.mainController}>
						<PlayerIconButton
							className={s.secondaryButton}
							onClick={() => skip(-1)}
							ariaLabel={`Назад ${skipSeconds} секунд`}
						>
							<IconRewindBackward15 size={24} />
						</PlayerIconButton>
						<PlayerIconButton
							className={s.mainButton}
							onClick={togglePlay}
							ariaLabel={isPlaying ? 'Пауза' : 'Воспроизвести'}
						>
							<PlayPauseIndicator size={28} isPlaying={isPlaying} />
						</PlayerIconButton>
						<PlayerIconButton
							className={s.secondaryButton}
							onClick={() => skip(1)}
							ariaLabel={`Вперёд ${skipSeconds} секунд`}
						>
							<IconRewindForward15 size={24} />
						</PlayerIconButton>
					</div>

					{/* Bottom progress bar */}
					<ProgressBar currentTime={currentTime} duration={duration} buffered={buffered} onSeek={handleSeek} />

					{/* Desktop bottom controllers */}
					<div className={s.desktopControllers}>
						<div className={s.leftGroup}>
							<PlayerIconButton onClick={() => skip(-1)} ariaLabel={`Назад ${skipSeconds} секунд`}>
								<IconRewindBackward15 size={24} />
							</PlayerIconButton>
							<PlayerIconButton
								className={s.mainButtonDesktop}
								onClick={togglePlay}
								ariaLabel={isPlaying ? 'Пауза' : 'Воспроизвести'}
							>
								<PlayPauseIndicator size={24} isPlaying={isPlaying} />
							</PlayerIconButton>
							<PlayerIconButton onClick={() => skip(1)} ariaLabel={`Вперёд ${skipSeconds} секунд`}>
								<IconRewindForward15 size={24} />
							</PlayerIconButton>
						</div>

						<div className={s.rightGroup}>
							<VolumeControl
								volume={volume}
								isMuted={isMuted}
								onToggleMute={toggleMute}
								onVolumeChange={handleVolumeChange}
							/>
							{allowSpeed && (
								<PlaybackRateMenu
									playbackRate={playbackRate}
									show={showSettings}
									onToggle={() => setShowSettings((v) => !v)}
									onSelect={changePlaybackRate}
								/>
							)}
							{allowFullscreen && (
								<PlayerIconButton
									className={s.desktopOnly}
									onClick={toggleFullscreen}
									ariaLabel={isFullscreen ? 'Выйти из полноэкранного режима' : 'Полноэкранный режим'}
								>
									{isFullscreen ? <IconMinimize size={24} /> : <IconMaximize size={24} />}
								</PlayerIconButton>
							)}
							{onClose && (
								<PlayerIconButton className={s.desktopOnly} onClick={onClose} ariaLabel="Закрыть">
									<IconX size={24} />
								</PlayerIconButton>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};
